import { useEffect, useState } from "react";
import { Download, Share, Plus, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { isStandalone, detectInstallPlatform } from "@/lib/pwa";

// Voce discreta "Installa l'app" per la schermata di accesso.
// Non tocca manifest/service worker: usa solo l'evento nativo beforeinstallprompt (Android/desktop)
// oppure una breve guida per iOS/iPadOS (Safari non espone il prompt nativo).
export function InstallApp() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);
  const [dockOpen, setDockOpen] = useState(false);
  const [genericOpen, setGenericOpen] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (isStandalone() || installed) return null;

  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const platform = detectInstallPlatform();
  const isIOS = platform === "ios";

  // Safari desktop su macOS (Sonoma 14+): niente beforeinstallprompt, ma "Aggiungi al Dock".
  const isMacSafari =
    platform === "desktop" &&
    /Macintosh/.test(ua) &&
    /Safari/.test(ua) &&
    !/Chrome|Chromium|CriOS|FxiOS|Firefox|Edg|OPR|Android/i.test(ua);

  const canPrompt = !!deferred;
  // La voce "Installa l'app" è SEMPRE presente nella schermata di accesso (a meno che l'app sia già
  // installata/standalone). Il click instrada al percorso corretto: prompt nativo se disponibile,
  // guida iOS, guida Dock su Safari macOS, oppure una guida generica per gli altri browser.

  const handleClick = async () => {
    if (canPrompt) {
      deferred.prompt();
      try {
        const { outcome } = await deferred.userChoice;
        if (outcome === "accepted") setDeferred(null);
      } catch (_) {}
    } else if (isIOS) {
      setIosOpen(true);
    } else if (isMacSafari) {
      setDockOpen(true);
    } else {
      setGenericOpen(true);
    }
  };

  return (
    <>
      <div className="mt-5 flex justify-center">
        <button
          type="button"
          onClick={handleClick}
          data-testid="install-app-btn"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary/70 hover:text-primary transition-colors"
        >
          <Download className="h-4 w-4" />
          Installa l'app
        </button>
      </div>

      <Dialog open={iosOpen} onOpenChange={setIosOpen}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="ios-install-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Installa Orbit sul tuo dispositivo</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Aggiungi Orbit alla schermata Home in tre semplici passaggi.
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 mt-1">
            <li className="flex items-center gap-3 text-sm text-slate-700">
              <span className="h-8 w-8 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Share className="h-4 w-4" />
              </span>
              <span>Tocca il pulsante <span className="font-semibold">Condividi</span> di Safari.</span>
            </li>
            <li className="flex items-center gap-3 text-sm text-slate-700">
              <span className="h-8 w-8 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Plus className="h-4 w-4" />
              </span>
              <span>Seleziona <span className="font-semibold">Aggiungi alla schermata Home</span>.</span>
            </li>
            <li className="flex items-center gap-3 text-sm text-slate-700">
              <span className="h-8 w-8 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Check className="h-4 w-4" />
              </span>
              <span>Tocca <span className="font-semibold">Aggiungi</span>.</span>
            </li>
          </ol>
          <Button
            onClick={() => setIosOpen(false)}
            data-testid="ios-install-ok"
            className="w-full rounded-lg h-10 mt-2 font-semibold"
          >
            Ho capito
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={dockOpen} onOpenChange={setDockOpen}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="dock-install-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Installa Orbit sul Mac</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Con Safari su macOS puoi aggiungere Orbit al Dock come app.
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 mt-1">
            <li className="flex items-center gap-3 text-sm text-slate-700">
              <span className="h-8 w-8 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Share className="h-4 w-4" />
              </span>
              <span>Apri il menu <span className="font-semibold">Condividi</span> di Safari.</span>
            </li>
            <li className="flex items-center gap-3 text-sm text-slate-700">
              <span className="h-8 w-8 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Plus className="h-4 w-4" />
              </span>
              <span>Seleziona <span className="font-semibold">Aggiungi al Dock</span>.</span>
            </li>
            <li className="flex items-center gap-3 text-sm text-slate-700">
              <span className="h-8 w-8 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Check className="h-4 w-4" />
              </span>
              <span>Conferma: Orbit si aprirà come app in finestra dedicata.</span>
            </li>
          </ol>
          <Button
            onClick={() => setDockOpen(false)}
            data-testid="dock-install-ok"
            className="w-full rounded-lg h-10 mt-2 font-semibold"
          >
            Ho capito
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={genericOpen} onOpenChange={setGenericOpen}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="generic-install-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Installa Orbit come app</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Puoi aggiungere Orbit come app direttamente dal tuo browser.
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 mt-1">
            <li className="flex items-center gap-3 text-sm text-slate-700">
              <span className="h-8 w-8 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Plus className="h-4 w-4" />
              </span>
              <span>Apri il <span className="font-semibold">menu del browser</span> (icona ⋮ o l'icona di installazione nella barra degli indirizzi).</span>
            </li>
            <li className="flex items-center gap-3 text-sm text-slate-700">
              <span className="h-8 w-8 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Download className="h-4 w-4" />
              </span>
              <span>Scegli <span className="font-semibold">Installa app</span>{platform === "android" && <> oppure <span className="font-semibold">Aggiungi a schermata Home</span></>}.</span>
            </li>
            <li className="flex items-center gap-3 text-sm text-slate-700">
              <span className="h-8 w-8 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Check className="h-4 w-4" />
              </span>
              <span>Conferma per aggiungere Orbit al tuo dispositivo.</span>
            </li>
          </ol>
          <Button
            onClick={() => setGenericOpen(false)}
            data-testid="generic-install-ok"
            className="w-full rounded-lg h-10 mt-2 font-semibold"
          >
            Ho capito
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
